import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "@/components/ui/toast";
import { ws } from "@/lib/ws";
import type { ApiKeyMeta, Scope } from "@shared/protocol";
import { API_KEY_SCOPES } from "@shared/protocol";
import { relativeTime } from "@/lib/utils";

function ApiKeyRevealDialog({
  raw,
  onClose,
}: {
  raw: string | null;
  onClose: () => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <Dialog open={raw !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent hideCloseButton>
        <DialogHeader>
          <DialogTitle>Your new API key</DialogTitle>
          <DialogDescription>
            Copy this key now. It will never be shown again — even we don&apos;t
            store the raw value.
          </DialogDescription>
        </DialogHeader>
        <div className="my-4 p-3 bg-muted rounded-md font-mono text-sm break-all">
          {raw}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            if (!raw) return;
            await navigator.clipboard.writeText(raw);
            toast("Copied");
          }}
        >
          Copy to clipboard
        </Button>
        <div className="flex items-center gap-2 mt-4">
          <input
            type="checkbox"
            id="ack"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <Label htmlFor="ack">I have copied this key</Label>
        </div>
        <DialogFooter>
          <Button disabled={!acknowledged} onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKeyMeta[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScopes, setNewKeyScopes] = useState<Scope[]>(["compile:submit"]);
  const [editKey, setEditKey] = useState<ApiKeyMeta | null>(null);
  const [editScopes, setEditScopes] = useState<Scope[]>([]);
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // compile:submit is baseline for everyone; these are the ADDITIONAL scopes
  // (compile:conformance / source:upload) this member's custom role grants.
  // Empty for every regular org and for any super-org member without a
  // grant — in that case every key can only ever be compile:submit, so the
  // whole scope picker would be a no-op and is hidden entirely (see below).
  const [grantedGatedScopes, setGrantedGatedScopes] = useState<Scope[]>([]);
  const selectableScopes = API_KEY_SCOPES.filter(
    (s) => s.value === "compile:submit" || grantedGatedScopes.includes(s.value),
  );

  function toggleScope(scope: Scope) {
    setNewKeyScopes((cur) =>
      cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope],
    );
  }

  function toggleEditScope(scope: Scope) {
    setEditScopes((cur) =>
      cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope],
    );
  }

  function openEdit(k: ApiKeyMeta) {
    setEditKey(k);
    setEditScopes(k.scopes);
  }

  useEffect(() => {
    const off = ws.on("keys.state", (m) => {
      setKeys(m.keys);
      setGrantedGatedScopes(m.grantedGatedScopes);
    });
    const offCreated = ws.on("keys.created", (m) => {
      setRawKey(m.raw);
      setShowCreate(false);
      setNewKeyName("");
      setNewKeyScopes(["compile:submit"]);
      setKeys((prev) => [m.key, ...prev]);
    });
    const offRotated = ws.on("keys.rotated", (m) => {
      setRawKey(m.raw);
      setKeys((prev) => prev.map((k) => (k.id === m.old_id ? m.key : k)));
    });
    const offRevoked = ws.on("keys.revoked", (m) => {
      setKeys((prev) =>
        prev.map((k) => (k.id === m.id ? { ...k, revoked_at: Date.now() } : k)),
      );
    });
    const offDeleted = ws.on("keys.deleted", (m) => {
      setKeys((prev) => prev.filter((k) => k.id !== m.id));
    });
    const offUpdated = ws.on("keys.updated", (m) => {
      setKeys((prev) => prev.map((k) => (k.id === m.key.id ? m.key : k)));
      setEditKey(null);
    });
    const offErr = ws.on("error", (m) => {
      if (m.req_type.startsWith("keys.")) setError(m.message);
    });
    ws.send({ type: "keys.list" });
    return () => {
      off();
      offCreated();
      offRotated();
      offRevoked();
      offDeleted();
      offUpdated();
      offErr();
    };
  }, []);

  function handleCreate() {
    if (!newKeyName.trim() || newKeyScopes.length === 0) return;
    ws.send({
      type: "keys.create",
      name: newKeyName.trim(),
      scopes: newKeyScopes,
    });
  }

  function handleRotate(id: string) {
    if (
      !confirm(
        "Rotate this key? The current key will stop working immediately.",
      )
    )
      return;
    ws.send({ type: "keys.rotate", id });
  }

  function handleRevoke(id: string) {
    if (!confirm("Revoke this key permanently?")) return;
    ws.send({ type: "keys.revoke", id });
  }

  function handleDelete(id: string) {
    if (
      !confirm(
        "Delete this key permanently? This cannot be undone and removes it entirely.",
      )
    )
      return;
    ws.send({ type: "keys.delete", id });
  }

  function handleSaveScopes() {
    if (!editKey || editScopes.length === 0) return;
    ws.send({ type: "keys.update", id: editKey.id, scopes: editScopes });
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">API Keys</h1>
          <p className="text-sm text-muted-foreground">
            Long-lived keys for local development and simple scripts. For CI/CD,
            prefer{" "}
            <a href="/credentials" className="underline">
              OIDC credentials
            </a>
            .
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>New key</Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Your keys</CardTitle>
        </CardHeader>
        <CardContent>
          {keys.length === 0 ? (
            <p className="text-sm text-muted-foreground">No keys yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell className="font-medium">{k.name}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {k.prefix}…
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {k.scopes.length === 0 ? (
                          <span className="text-xs text-muted-foreground">
                            none
                          </span>
                        ) : (
                          k.scopes.map((s) => (
                            <Badge
                              key={s}
                              variant="secondary"
                              className="font-mono text-[10px]"
                            >
                              {s}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(k.created_at)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(k.last_used)}
                    </TableCell>
                    <TableCell>
                      {k.revoked_at ? (
                        <Badge variant="destructive">revoked</Badge>
                      ) : (
                        <Badge variant="success">active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!k.revoked_at && (
                        <>
                          {selectableScopes.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(k)}
                            >
                              Edit
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRotate(k.id)}
                          >
                            Rotate
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevoke(k.id)}
                          >
                            Revoke
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(k.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>
              Give this key a name you&apos;ll recognise later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 my-4">
            <Label htmlFor="keyname">Name</Label>
            <Input
              id="keyname"
              placeholder="my-laptop"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
            />
          </div>
          {selectableScopes.length > 1 && (
            <div className="space-y-2 mb-4">
              <Label>Scopes</Label>
              <p className="text-xs text-muted-foreground">
                What this key is allowed to do. `compile:submit` is required to
                run <span className="font-mono">pure build</span>.
              </p>
              <div className="space-y-2 mt-1">
                {selectableScopes.map((s) => (
                  <label
                    key={s.value}
                    htmlFor={`scope-${s.value}`}
                    className="flex items-start gap-2"
                  >
                    <input
                      type="checkbox"
                      id={`scope-${s.value}`}
                      className="mt-0.5"
                      checked={newKeyScopes.includes(s.value)}
                      onChange={() => toggleScope(s.value)}
                    />
                    <span className="leading-tight">
                      <span className="font-mono text-xs">{s.value}</span>
                      <span className="block text-xs text-muted-foreground">
                        {s.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {newKeyScopes.length === 0 && (
                <p className="text-xs text-destructive">
                  Select at least one scope.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!newKeyName.trim() || newKeyScopes.length === 0}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editKey !== null}
        onOpenChange={(o) => !o && setEditKey(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit permissions</DialogTitle>
            <DialogDescription>
              Update what <span className="font-medium">{editKey?.name}</span>{" "}
              is allowed to do. Takes effect immediately for the existing key.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 my-4">
            {selectableScopes.map((s) => (
              <label
                key={s.value}
                htmlFor={`edit-scope-${s.value}`}
                className="flex items-start gap-2"
              >
                <input
                  type="checkbox"
                  id={`edit-scope-${s.value}`}
                  className="mt-0.5"
                  checked={editScopes.includes(s.value)}
                  onChange={() => toggleEditScope(s.value)}
                />
                <span className="leading-tight">
                  <span className="font-mono text-xs">{s.value}</span>
                  <span className="block text-xs text-muted-foreground">
                    {s.description}
                  </span>
                </span>
              </label>
            ))}
            {editScopes.length === 0 && (
              <p className="text-xs text-destructive">
                Select at least one scope.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditKey(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveScopes}
              disabled={editScopes.length === 0}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ApiKeyRevealDialog raw={rawKey} onClose={() => setRawKey(null)} />
    </div>
  );
}
